import React, { useState, useEffect } from 'react';
import { API_BASE_URL, API_URL } from "../config";
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { 
  FaGavel, FaPlus, FaEye, FaClock, FaCheckCircle,
  FaTimes, FaUsers, FaTruck, FaWeightHanging,
  FaMapMarkerAlt, FaMoneyBillWave, FaCalendarAlt,
  FaSpinner, FaInfoCircle, FaArrowRight
} from 'react-icons/fa';
import AdminLayout from '../layouts/AdminLayout';

function TenderDashboard({ onLogout }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tenders, setTenders] = useState([]);
  const [user, setUser] = useState({});
  const [stats, setStats] = useState({
    open: 0,
    closed: 0,
    awarded: 0,
    total: 0
  });

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setUser(userData);
    fetchTenders();
  }, []);

  const fetchTenders = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/tenders`);
      const data = await response.json();
      if (data.success) {
        setTenders(data.tenders || []);
        const open = data.tenders?.filter(t => t.status === 'open').length || 0;
        const closed = data.tenders?.filter(t => t.status === 'closed').length || 0;
        const awarded = data.tenders?.filter(t => t.status === 'awarded').length || 0;
        setStats({ open, closed, awarded, total: data.tenders?.length || 0 });
      }
    } catch (err) {
      console.error('Error fetching tenders:', err);
      toast.error('Failed to load tenders');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      'open': 'bg-green-100 text-green-600',
      'closed': 'bg-yellow-100 text-yellow-600',
      'awarded': 'bg-blue-100 text-blue-600',
      'cancelled': 'bg-red-100 text-red-600'
    };
    return `px-2 py-1 rounded-full text-xs font-semibold ${styles[status] || 'bg-gray-100 text-gray-600'}`;
  };

  const getStatusLabel = (status) => {
    const labels = {
      'open': 'Open',
      'closed': 'Closed',
      'awarded': 'Awarded',
      'cancelled': 'Cancelled'
    };
    return labels[status] || status;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-ZA', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <AdminLayout activeTab="tenders" onLogout={onLogout} user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <FaSpinner className="animate-spin text-4xl text-green-500 mx-auto mb-4" />
            <p className="text-gray-500">Loading tenders...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout activeTab="tenders" onLogout={onLogout} user={user}>
      {/* Header */}
      <div className="flex flex-wrap justify-between items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: '#0A1929' }}>
            <FaGavel className="inline mr-2" style={{ color: '#00A86B' }} />
            Tender Dashboard
          </h1>
          <p className="text-sm" style={{ color: '#4A5568' }}>Manage tenders and review subcontractor bids</p>
        </div>
          {(user.role === 'super_admin' || (user.permissions || []).includes('create_tenders')) && (
            <Link
              to="/create-tender"
              className="flex items-center gap-2 px-4 py-2 bg-navy text-white rounded-lg text-sm hover:bg-green hover:text-navy transition-colors"
            >
              <FaPlus size={14} /> Create Tender
            </Link>
          )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl border p-4 shadow-sm" style={{ borderColor: '#E8ECEF' }}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500">Total Tenders</p>
              <p className="text-2xl font-bold text-navy">{stats.total}</p>
            </div>
            <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center text-blue-600">
              <FaGavel size={18} />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border p-4 shadow-sm" style={{ borderColor: '#E8ECEF' }}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500">Open</p>
              <p className="text-2xl font-bold text-green-600">{stats.open}</p>
            </div>
            <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center text-green-600">
              <FaClock size={18} />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border p-4 shadow-sm" style={{ borderColor: '#E8ECEF' }}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500">Awarded</p>
              <p className="text-2xl font-bold text-blue-600">{stats.awarded}</p>
            </div>
            <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center text-blue-600">
              <FaCheckCircle size={18} />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border p-4 shadow-sm" style={{ borderColor: '#E8ECEF' }}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500">Closed</p>
              <p className="text-2xl font-bold text-yellow-600">{stats.closed}</p>
            </div>
            <div className="w-10 h-10 bg-yellow-50 rounded-lg flex items-center justify-center text-yellow-600">
              <FaTimes size={18} />
            </div>
          </div>
        </div>
      </div>

      {/* Tenders List */}
      {tenders.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center border" style={{ borderColor: '#E8ECEF' }}>
          <div className="text-6xl mb-4">📋</div>
          <h3 className="text-xl font-semibold mb-2" style={{ color: '#0A1929' }}>No Tenders Yet</h3>
          <p className="text-sm" style={{ color: '#4A5568' }}>Create your first tender to get started.</p>
          <Link
            to="/create-tender"
            className="mt-4 inline-block px-6 py-2 bg-navy text-white rounded-lg text-sm hover:bg-green hover:text-navy transition-colors"
          >
            <FaPlus className="inline mr-2" /> Create Tender
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          {tenders.map((tender) => {
            const job = tender.jobs;
            return (
              <div key={tender.id} className="bg-white rounded-xl border p-6 shadow-sm hover:shadow-md transition-shadow" style={{ borderColor: '#E8ECEF' }}>
                <div className="flex flex-wrap justify-between items-start gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-bold text-navy">{tender.tender_number}</h3>
                      <span className={getStatusBadge(tender.status)}>
                        {getStatusLabel(tender.status)}
                      </span>
                      {tender.tender_type === 'framework' && (
                        <span className="px-2 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-600">
                          Framework
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600">
                      {job?.commodity || 'Unknown'} • {tender.total_volume_mt || 0} MT
                    </p>
                    <div className="flex flex-wrap gap-4 text-sm text-gray-500 mt-2">
                      <span className="flex items-center gap-1">
                        <FaMapMarkerAlt size={14} /> {job?.origin} → {job?.destination}
                      </span>
                      <span className="flex items-center gap-1">
                        <FaCalendarAlt size={14} /> Deadline: {formatDate(tender.bidding_deadline)}
                      </span>
                      {tender.target_rate && (
                        <span className="flex items-center gap-1">
                          <FaMoneyBillWave size={14} /> Target: R{tender.target_rate}/MT
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <FaUsers size={14} /> Winners: {tender.number_of_winners}
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Link
                      to={`/tender-dashboard/${tender.id}`}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition-colors"
                    >
                      <FaEye size={14} /> View & Manage
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </AdminLayout>
  );
}

export default TenderDashboard;
